import React, { useState } from "react";
import { Alert, Button, Card, Form, InputGroup } from "react-bootstrap";
import useApi from "../hooks/useApi";
import "./ChangePasswordCard.css";

const ChangePasswordCard = ({
  endpoint,
  title = "Change Password",
  description = "Use your current password to set a new secure password.",
}) => {
  const { bearerAuth } = useApi();
  const [form, setForm] = useState({
    current_password: "",
    new_password: "",
    new_password_confirmation: "",
  });
  const [show, setShow] = useState({
    current_password: false,
    new_password: false,
    new_password_confirmation: false,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const onInput = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const toggleField = (key) => {
    setShow((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!form.current_password || !form.new_password || !form.new_password_confirmation) {
      setError("All password fields are required.");
      return;
    }
    if (form.new_password.length < 6) {
      setError("New password must be at least 6 characters.");
      return;
    }
    if (form.new_password !== form.new_password_confirmation) {
      setError("New password and confirmation do not match.");
      return;
    }
    if (form.current_password === form.new_password) {
      setError("New password must be different from current password.");
      return;
    }

    setLoading(true);
    try {
      const res = await bearerAuth(endpoint, "POST", form);
      setSuccess(res?.data?.message || "Password changed successfully.");
      setForm({
        current_password: "",
        new_password: "",
        new_password_confirmation: "",
      });
    } catch (err) {
      setError(err?.message || "Failed to change password.");
    } finally {
      setLoading(false);
    }
  };

  const renderPasswordField = (name, label, placeholder) => (
    <Form.Group className="mb-3">
      <Form.Label>{label}</Form.Label>
      <InputGroup>
        <Form.Control
          type={show[name] ? "text" : "password"}
          name={name}
          value={form[name]}
          onChange={onInput}
          placeholder={placeholder}
          autoComplete="off"
        />
        <Button
          type="button"
          variant="outline-secondary"
          onClick={() => toggleField(name)}
          aria-label={`Toggle ${label}`}
        >
          <i className={`bi ${show[name] ? "bi-eye-slash" : "bi-eye"}`}></i>
        </Button>
      </InputGroup>
    </Form.Group>
  );

  return (
    <Card className="change-password-card">
      <Card.Header>
        <h5 className="mb-1">{title}</h5>
        <p className="mb-0 text-muted small">{description}</p>
      </Card.Header>
      <Card.Body>
        {error ? <Alert variant="danger">{error}</Alert> : null}
        {success ? <Alert variant="success">{success}</Alert> : null}

        <Form onSubmit={onSubmit}>
          {renderPasswordField("current_password", "Current Password", "Enter current password")}
          {renderPasswordField("new_password", "New Password", "Enter new password")}
          {renderPasswordField("new_password_confirmation", "Confirm New Password", "Re-enter new password")}

          <div className="d-flex justify-content-end">
            <Button type="submit" variant="primary" disabled={loading}>
              {loading ? "Updating..." : "Update Password"}
            </Button>
          </div>
        </Form>
      </Card.Body>
    </Card>
  );
};

export default ChangePasswordCard;
